/**
 * Brushable histogram. Renders an ECharts bar chart over fixed bin counts.
 * `dataZoom` provides the brush; on change it reports the bin index range
 * (start..end inclusive) to the parent.
 */
import { useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

interface Props {
  title: string
  bins: number[]                          // bin counts (length N)
  binWidth: number                        // axis units per bin
  unitLabel: string                       // e.g. 'days' (shown in the tooltip)
  /** Optional per-bin x-axis label; overrides the default `i * binWidth`. */
  binLabel?: (binIndex: number) => string
  /** Currently brushed bin range as [start, end] inclusive, or null for all. */
  brush: [number, number] | null
  onBrushChange: (range: [number, number] | null) => void
  /** Color */
  color?: string
  height?: number
}

export default function BrushHistogram({
  title, bins, binWidth, unitLabel, binLabel, brush, onBrushChange,
  color = 'oklch(0.55 0.18 240)', height = 130,
}: Props) {
  const N = bins.length

  const xLabels = useMemo(
    () => binLabel
      ? bins.map((_, i) => binLabel(i))
      : bins.map((_, i) => `${(i * binWidth).toFixed(binWidth < 1 ? 1 : 0)}`),
    [bins, binWidth, binLabel]
  )

  const option = useMemo(() => {
    const brushStart = brush ? (brush[0] / Math.max(N - 1, 1)) * 100 : 0
    const brushEnd = brush ? (brush[1] / Math.max(N - 1, 1)) * 100 : 100
    return {
      grid: { left: 30, right: 8, top: 22, bottom: 38 },
      title: { text: title, left: 'left', top: 0, textStyle: { fontSize: 11, fontWeight: 600 } },
      xAxis: {
        type: 'category',
        data: xLabels,
        axisLabel: { fontSize: 9 },
        name: unitLabel,
        nameLocation: 'middle',
        nameGap: 22,
        nameTextStyle: { fontSize: 9 },
      },
      yAxis: { type: 'value', show: false },
      series: [
        {
          type: 'bar',
          data: bins,
          itemStyle: { color },
          barCategoryGap: '10%',
        },
      ],
      dataZoom: [
        {
          type: 'slider',
          xAxisIndex: 0,
          start: brushStart,
          end: brushEnd,
          height: 14,
          bottom: 0,
          showDetail: false,
          brushSelect: false,
        },
      ],
      tooltip: { trigger: 'axis', formatter: (p: { name?: string; value?: number }[]) => {
          const unit = unitLabel ? ` ${unitLabel}` : ''
          return `${p[0].name}${unit} — ${(p[0].value ?? 0).toLocaleString()} awards`
        } },
    }
  }, [bins, brush, color, N, title, unitLabel, xLabels])

  const onEvents = useMemo(
    () => ({
      dataZoom: (params: { start?: number; end?: number; batch?: { start: number; end: number }[] }) => {
        const start = params.start ?? params.batch?.[0]?.start ?? 0
        const end = params.end ?? params.batch?.[0]?.end ?? 100
        const startBin = Math.round((start / 100) * (N - 1))
        const endBin = Math.round((end / 100) * (N - 1))
        if (startBin <= 0 && endBin >= N - 1) {
          onBrushChange(null)
        } else {
          onBrushChange([startBin, endBin])
        }
      },
    }),
    [N, onBrushChange]
  )

  return <ReactECharts option={option} onEvents={onEvents} style={{ height }} notMerge />
}
